import type { ForeignDocumentType, Gender, MaritalStatus, ReferralChannel } from './api'

/**
 * Os rótulos do cadastro pelo link: o valor do contrato nunca vai cru para a tela (T21). As listas
 * são as do cadastro da clínica (`apps/web/src/features/patients/labels.ts`), na voz de quem fala
 * com a paciente.
 */

export const MARITAL_STATUS_LABELS: Record<MaritalStatus, string> = {
  single: 'Solteira(o)',
  married: 'Casada(o)',
  domestic_partnership: 'União estável',
  separated: 'Separada(o)',
  divorced: 'Divorciada(o)',
  widowed: 'Viúva(o)',
}

/** "Prefiro não informar" é o `not_provided`, que nunca conta como resposta (P-PAC-12). */
export const GENDER_LABELS: Record<Gender, string> = {
  female: 'Feminino',
  male: 'Masculino',
  other: 'Outro',
  not_provided: 'Prefiro não informar',
}

export const FOREIGN_DOCUMENT_TYPE_LABELS: Record<ForeignDocumentType, string> = {
  passport: 'Passaporte',
  rne: 'RNE',
  crnm: 'CRNM',
  other: 'Outro documento',
}

export const REFERRAL_CHANNEL_LABELS: Record<ReferralChannel, string> = {
  referral: 'Indicação',
  social_media: 'Redes sociais',
  instagram: 'Instagram',
  google: 'Google',
  ad: 'Anúncio',
  website: 'Site',
  storefront: 'Fachada da clínica',
  event: 'Evento',
  other: 'Outro',
}

/** Os DDIs do cadastro; o Brasil é o padrão (RT-17). */
export const DEFAULT_DIAL_CODE = '+55'

export const DIAL_CODES: { code: string; label: string }[] = [
  { code: '+55', label: 'Brasil +55' },
  { code: '+54', label: 'Argentina +54' },
  { code: '+591', label: 'Bolívia +591' },
  { code: '+56', label: 'Chile +56' },
  { code: '+57', label: 'Colômbia +57' },
  { code: '+595', label: 'Paraguai +595' },
  { code: '+51', label: 'Peru +51' },
  { code: '+598', label: 'Uruguai +598' },
  { code: '+1', label: 'Estados Unidos e Canadá +1' },
  { code: '+52', label: 'México +52' },
  { code: '+351', label: 'Portugal +351' },
  { code: '+34', label: 'Espanha +34' },
  { code: '+33', label: 'França +33' },
  { code: '+39', label: 'Itália +39' },
  { code: '+49', label: 'Alemanha +49' },
  { code: '+44', label: 'Reino Unido +44' },
  { code: '+41', label: 'Suíça +41' },
  { code: '+81', label: 'Japão +81' },
]

/** As 27 UFs, a lista da API (RT-17). */
export const STATES = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
] as const

/** ISO 3166-1 alfa-2, a lista do cadastro da clínica. O nome sai do `Intl`, em português. */
const COUNTRY_CODES =
  'AD AE AF AG AI AL AM AO AR AS AT AU AW AZ BA BB BD BE BF BG BH BI BJ BM BN BO BR BS BT BW BY BZ CA CD CF CG CH CI CL CM CN CO CR CU CV CW CY CZ DE DJ DK DM DO DZ EC EE EG ER ES ET FI FJ FM FR GA GB GD GE GF GH GM GN GP GQ GR GT GW GY HK HN HR HT HU ID IE IL IN IQ IR IS IT JM JO JP KE KG KH KM KN KP KR KW KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MG MK ML MM MN MO MQ MR MT MU MV MW MX MY MZ NA NE NG NI NL NO NP NZ OM PA PE PG PH PK PL PR PS PT PY QA RE RO RS RU RW SA SB SC SD SE SG SI SK SL SM SN SO SR SS ST SV SY SZ TD TG TH TJ TL TM TN TO TR TT TW TZ UA UG US UY UZ VA VC VE VN VU WS YE ZA ZM ZW'

function regionNames(): Intl.DisplayNames | null {
  try {
    return new Intl.DisplayNames(['pt-BR'], { type: 'region' })
  } catch {
    return null
  }
}

/** O nome do país em português, a partir do código ISO alfa-2. */
export function countryName(code: string): string {
  try {
    return regionNames()?.of(code) ?? code
  } catch {
    return code
  }
}

/** Os países em ordem alfabética do nome em português. */
export function countryOptions(): { code: string; name: string }[] {
  const names = regionNames()
  return COUNTRY_CODES.split(' ')
    .map((code) => ({ code, name: names?.of(code) ?? code }))
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
}
